/**
 * Rich message content — orchestrates structured chat response rendering.
 *
 * v2 sections (deterministic-first pipeline):
 *   1. Scripture blockquote (itIsWritten — DB lookup, not model)
 *   2. Discussion body with inline Strong's chips (mlResponse)
 *   3. Strong's analysis prose (second SLM call)
 *   4. Textual variant section (deterministic SQLite)
 *   5. Cross-references (deterministic)
 *   6. Legacy: commentary, validation badge
 *
 * Falls back to plain text when no structured content is available.
 */

import { useMemo } from 'react';
import type { RichResponseMetadata, VariantAnalysis, PericopeInfo } from '@skjv/common';
import { parseMessageSegments, splitScriptureHeader } from './parse-message-segments';
import { ScriptureBlock } from './scripture-block';
import { StrongsSection } from './strongs-section';
import { CrossRefsSection } from './cross-refs-section';
import { CommentarySection, deriveAnchorContext } from './commentary-section';
import { ValidationBadge } from './validation-badge';
import { InlineVerseRef } from './inline-verse-ref';
import { VariantAnalysisSection } from './variant-analysis-section';
import { buildStrongsMap, renderSegment } from './render-segment';
import { ReportButton } from './report-button';
import type { ReportInput } from './build-report-markdown';
import type { ReportRunMeta } from '@state/chat';

interface ContentChunk {
  section: string;
  content: string;
  reference?: string;
}

interface RichMessageContentProps {
  content: string;
  metadata?: RichResponseMetadata;
  commentary?: string;
  /** Pre-injection model output — preferred over `commentary` for the
   *  Report dialog's AI Response. */
  rawModelBody?: string;
  /** The prompt this response was generated from — shown in the report. */
  prompt?: string;
  /** What the run did — the report's Metadata block. */
  reportMeta?: ReportRunMeta;
  /** The reader's "Any thoughts?" seed notes — an input the report keeps. */
  readerThoughts?: string;
  contentChunks?: ContentChunk[];
  originalQuery?: string;
  // v2 ChatResponse fields
  itIsWritten?: string;
  reference?: string;
  strongsAnalysis?: string | null;
  textualVariant?: VariantAnalysis | null;
  crossReferences?: string[];
  crossRefTexts?: Record<string, string>;
  pericope?: PericopeInfo | null;
  variantSummary?: string;
  /** V6.4 — passage per-verse KJV for scripture-form anchor labels. */
  passageVerses?: Array<{ verse: number; text: string }>;
  /** Render only the commentary section + report button — for follow-up
   *  bubbles that sit beneath a scripture-first bubble. Skips the
   *  scripture block, pericope, cross-refs, strong's, and variant
   *  sections (those are already shown in the scripture-first bubble
   *  above and re-rendering them creates visual duplication). */
  commentaryOnly?: boolean;
  /** False when a card above owns the Report action (the pericope study
   *  toolbar) — otherwise the same action would appear twice. */
  showReport?: boolean;
}

export function RichMessageContent({
  content,
  metadata,
  commentary,
  rawModelBody,
  prompt,
  reportMeta,
  readerThoughts,
  contentChunks,
  originalQuery,
  itIsWritten,
  reference,
  strongsAnalysis,
  textualVariant,
  crossReferences,
  crossRefTexts,
  pericope,
  variantSummary,
  passageVerses,
  commentaryOnly,
  showReport = true,
}: RichMessageContentProps) {
  const strongsMap = useMemo(() => buildStrongsMap(metadata?.strongsRefs), [metadata]);

  // v2: use itIsWritten for scripture block; v1: use content chunks
  const scriptureChunks = useMemo(
    () => contentChunks?.filter((c) => c.section === 'scripture') ?? [],
    [contentChunks],
  );

  const parsed = useMemo(() => {
    const headerResult = splitScriptureHeader(content);
    const bodyText = headerResult ? headerResult.body : content;
    const segments = parseMessageSegments(bodyText);
    return { header: headerResult?.header ?? null, segments };
  }, [content]);

  const hasScripture = itIsWritten || scriptureChunks.length > 0 || parsed.header;
  const hasNotables = metadata?.notableStrongs && metadata.notableStrongs.length > 0;
  const hasStrongs = metadata?.strongsRefs && metadata.strongsRefs.length > 0;

  const hasStructuredContent =
    hasScripture ||
    hasNotables || hasStrongs ||
    strongsAnalysis ||
    textualVariant ||
    pericope ||
    (crossReferences && crossReferences.length > 0) ||
    (metadata?.crossRefs && metadata.crossRefs.length > 0) ||
    commentary;

  const reportInput = useMemo<ReportInput | null>(() => {
    const aiBody = rawModelBody ?? commentary ?? content;
    if (!aiBody || !reference) return null;
    const verseText = itIsWritten ?? parsed.header?.verseText ?? '';
    const xrefs = crossReferences && crossReferences.length > 0
      ? crossReferences.map((ref) => ({ ref, text: crossRefTexts?.[ref] }))
      : (metadata?.crossRefs?.map((cr) => ({ ref: cr.to, text: cr.toText })) ?? []);
    const variantLine = variantSummary
      ?? (textualVariant
        ? `${textualVariant.shortSummary || textualVariant.category} (impact: ${textualVariant.impact})`
        : null);
    return {
      reference,
      verseText,
      pericope: pericope
        ? {
            title: pericope.title,
            book: pericope.book,
            chapter: pericope.chapter,
            startVerse: pericope.startVerse,
            endVerse: pericope.endVerse,
            summary: pericope.summary,
          }
        : null,
      crossRefs: xrefs,
      variantSummary: variantLine,
      aiResponse: aiBody,
      prompt,
      meta: reportMeta,
      readerThoughts,
    };
  }, [commentary, rawModelBody, prompt, reportMeta, readerThoughts, content, reference, itIsWritten, parsed.header, crossReferences, crossRefTexts, metadata, pericope, textualVariant, variantSummary]);

  // Plain text fallback — no structured content detected
  if (!hasStructuredContent && parsed.segments.length <= 1) {
    return <p className="chat-bubble-text">{content}</p>;
  }

  // Commentary-only mode — the scripture-first bubble above us already
  // rendered scripture / pericope / cross-refs / variants. Render only
  // the commentary and the report button to avoid duplication.
  if (commentaryOnly) {
    return (
      <div className="rich-message">
        {commentary && (
          <CommentarySection
            commentary={commentary}
            anchorContext={deriveAnchorContext(pericope, reference, passageVerses)}
          />
        )}
        {showReport && reportInput && (
          <ReportButton input={reportInput} originalQuery={originalQuery} />
        )}
      </div>
    );
  }

  return (
    <div className="rich-message">
      {/* Scripture block: v2 itIsWritten > v1 content chunks > v1 parsed header */}
      {itIsWritten ? (
        <ScriptureBlock text={itIsWritten} reference={reference ?? ''} />
      ) : scriptureChunks.length > 0 ? (
        scriptureChunks.map((chunk, i) => (
          <ScriptureBlock
            key={`sc-${i}`}
            text={chunk.content}
            reference={chunk.reference ?? ''}
          />
        ))
      ) : parsed.header ? (
        <ScriptureBlock
          text={parsed.header.verseText}
          reference={parsed.header.reference}
        />
      ) : null}

      {/* Pericope context */}
      {pericope && (
        <div className="rich-message-section pericope-section">
          <h4 className="rich-message-section-title">
            Passage: {pericope.book} {pericope.chapter}:{pericope.startVerse}-{pericope.endVerse}
          </h4>
          {pericope.summary && <p className="pericope-summary">{pericope.summary}</p>}
        </div>
      )}

      {/* Model response body with inline Strong's chips */}
      <div className="rich-message-body">
        {parsed.segments.map((seg, i) => renderSegment(seg, i, strongsMap))}
      </div>

      {/* v2: Strong's prose analysis — chipify so H#### / G#### references
          render as native (pronunciation) chips with the same hover/click
          behavior as inline chips elsewhere. */}
      {strongsAnalysis && (
        <div className="rich-message-section">
          <h4 className="rich-message-section-title">Word Study</h4>
          <p className="strongs-prose">
            {parseMessageSegments(strongsAnalysis).map((seg, i) => renderSegment(seg, i, strongsMap))}
          </p>
        </div>
      )}

      {/* v1: Notable words / Strong's chips */}
      {!strongsAnalysis && hasNotables ? (
        <StrongsSection strongsRefs={metadata!.notableStrongs} title="Notable Words" />
      ) : !strongsAnalysis && hasStrongs ? (
        <StrongsSection strongsRefs={metadata!.strongsRefs} />
      ) : null}

      {/* v2: Textual variant analysis (deterministic) */}
      {textualVariant && <VariantAnalysisSection variant={textualVariant} />}

      {/* Cross-references: v2 string array or v1 structured refs */}
      {crossReferences && crossReferences.length > 0 ? (
        <div className="rich-message-section">
          <h4 className="rich-message-section-title">Cross-References</h4>
          <ul className="cross-ref-list">
            {crossReferences.map((ref, i) => (
              <li key={`xr-${i}`}><InlineVerseRef reference={ref} displayText={ref} /></li>
            ))}
          </ul>
        </div>
      ) : metadata?.crossRefs && metadata.crossRefs.length > 0 ? (
        <CrossRefsSection crossRefs={metadata.crossRefs} />
      ) : null}

      {commentary && (
        <CommentarySection
          commentary={commentary}
          anchorContext={deriveAnchorContext(pericope, reference, passageVerses)}
        />
      )}

      {metadata?.validation && <ValidationBadge validation={metadata.validation} />}

      {showReport && reportInput && (
        <ReportButton input={reportInput} originalQuery={originalQuery} />
      )}
    </div>
  );
}
